import { useEffect, useRef, useState } from "react";
import { useForm } from "@tanstack/react-form";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { useTranslations } from "use-intl";
import { z } from "zod";
import { Button } from "@lawline/ui/components/button";
import {
	Card,
	CardHeader,
	CardTitle,
	CardDescription,
	CardContent,
	CardFooter,
} from "@lawline/ui/components/card";
import { Alert, AlertTitle, AlertDescription } from "@lawline/ui/components/alert";
import { Field, FieldLabel, FieldGroup, FieldError } from "@lawline/ui/components/field";
import { NativeSelect, NativeSelectOption } from "@lawline/ui/components/native-select";
import { connectAuthClient } from "@platform/auth/client";
import { callbackLabel, oauthRedirect } from "@platform/core/oauth-redirect";

const selectionSchema = z.object({ organizationId: z.uuid() });
const clientSchema = z.object({ client_name: z.string().optional() });
const fetchOptions = () => ({ signal: AbortSignal.timeout(15_000), retry: 0 }) as const;
// The OAuth error codes the server sends to /connect/error. The page never shows the description
// that comes with them, since anyone can put words in a link.
const ERROR_MESSAGES = {
	invalid_client: "errorUnknownApp",
	client_disabled: "errorBlockedApp",
	unauthorized_client: "errorBlockedApp",
	invalid_redirect: "errorRedirect",
	invalid_target: "errorResource",
	invalid_request: "errorRequest",
	invalid_request_uri: "errorRequest",
	unsupported_response_type: "errorRequest",
	unsupported_prompt_select_account: "errorRequest",
} as const;

function errorMessage(step: string, code: string | null) {
	if (step !== "error") return "error";
	return code && Object.hasOwn(ERROR_MESSAGES, code)
		? ERROR_MESSAGES[code as keyof typeof ERROR_MESSAGES]
		: "errorRestart";
}

export function McpConnectPage() {
	const t = useTranslations("mcpConnect");
	const { step } = useParams({ from: "/connect/$step" });
	const [error, setError] = useState(false);
	const [pending, setPending] = useState(false);
	const continuing = useRef(false);
	const query = new URLSearchParams(window.location.search);
	const clientId = query.get("client_id");
	const callback = query.get("redirect_uri");
	const callbackOrigin = callbackLabel(callback);
	const validStep = ["organization", "continue", "consent"].includes(step);
	const session = useQuery({
		queryKey: ["mcp-connect", "session"],
		retry: false,
		queryFn: async () => {
			const result = await connectAuthClient.getSession({ fetchOptions: fetchOptions() });
			if (result.error || !result.data?.user) throw new Error("Sign-in is required.");
			return result.data;
		},
	});
	const organizations = useQuery({
		queryKey: ["mcp-connect", "organizations"],
		enabled: step === "organization" && !!session.data,
		retry: false,
		queryFn: async () => {
			const result = await connectAuthClient.organization.list({ fetchOptions: fetchOptions() });
			if (result.error || !result.data) throw new Error("Could not load organizations.");
			return result.data;
		},
	});
	const consent = useQuery({
		queryKey: ["mcp-connect", "consent", clientId],
		enabled: step === "consent" && !!session.data && !!clientId,
		retry: false,
		queryFn: async () => {
			const [client, organization] = await Promise.all([
				connectAuthClient.oauth2.publicClient({
					query: { client_id: clientId! },
					fetchOptions: fetchOptions(),
				}),
				connectAuthClient.organization.getFullOrganization({ fetchOptions: fetchOptions() }),
			]);
			if (client.error || organization.error || !organization.data)
				throw new Error("Could not load MCP approval.");
			return { client: clientSchema.parse(client.data), organization: organization.data };
		},
	});
	const form = useForm({
		defaultValues: { organizationId: "" },
		validators: { onSubmit: selectionSchema },
		onSubmit: async ({ value }) => {
			setError(false);
			try {
				const result = await connectAuthClient.organization.setActive({
					organizationId: value.organizationId,
					fetchOptions: fetchOptions(),
				});
				// The provider resumes the signed flow when the session changes, so the answer is
				// usually the next step of the flow rather than the organization.
				const url = !result.error && oauthRedirect(result.data, callback);
				if (url) return window.location.assign(url);
				if (result.error || result.data?.id !== value.organizationId)
					throw new Error("Organization selection failed.");
				window.location.assign(`/connect/continue${window.location.search}`);
			} catch {
				setError(true);
			}
		},
	});
	useEffect(() => {
		if (step !== "continue" || !session.data || continuing.current) return;
		continuing.current = true;
		void connectAuthClient.oauth2.continue({ postLogin: true, fetchOptions: fetchOptions() }).then(
			(result) => {
				const url = !result.error && oauthRedirect(result.data, callback);
				if (url) window.location.assign(url);
				else setError(true);
			},
			() => setError(true),
		);
	}, [step, session.data, callback]);
	async function approve(accept: boolean) {
		if (pending) return;
		setPending(true);
		setError(false);
		try {
			const result = await connectAuthClient.oauth2.consent({
				accept,
				fetchOptions: fetchOptions(),
			});
			const url = !result.error && oauthRedirect(result.data, callback);
			if (!url) throw new Error("MCP approval failed.");
			window.location.assign(url);
		} catch {
			setError(true);
		} finally {
			setPending(false);
		}
	}
	const failed =
		error || session.isError || organizations.isError || consent.isError || !validStep || !clientId;
	return (
		<main className="flex min-h-screen items-center justify-center p-6">
			<Card className="w-full max-w-lg">
				<CardHeader>
					<CardTitle>{t("title")}</CardTitle>
					<CardDescription>{t("description")}</CardDescription>
				</CardHeader>
				<CardContent className="flex flex-col gap-4">
					{failed ? (
						<Alert variant="destructive">
							<AlertTitle>{t("errorTitle")}</AlertTitle>
							<AlertDescription>{t(errorMessage(step, query.get("error")))}</AlertDescription>
						</Alert>
					) : null}
					{!failed &&
					(session.isPending ||
						(step === "organization" && organizations.isPending) ||
						(step === "consent" && consent.isPending)) ? (
						<p role="status">{t("loading")}</p>
					) : null}
					{!failed && step === "organization" && organizations.data?.length === 0 ? (
						<p>{t("noOrganizations")}</p>
					) : null}
					{!failed && step === "organization" && organizations.data ? (
						<form
							id="mcp-organization"
							onSubmit={(event) => {
								event.preventDefault();
								void form.handleSubmit();
							}}
						>
							<FieldGroup>
								<form.Field name="organizationId">
									{(field) => {
										const invalid = field.state.meta.isTouched && !field.state.meta.isValid;
										return (
											<Field data-invalid={invalid}>
												<FieldLabel htmlFor="mcp-organization-id">{t("organization")}</FieldLabel>
												<NativeSelect
													id="mcp-organization-id"
													value={field.state.value}
													onBlur={field.handleBlur}
													onChange={(event) => field.handleChange(event.target.value)}
													aria-invalid={invalid}
												>
													<NativeSelectOption value="">
														{t("selectOrganization")}
													</NativeSelectOption>
													{organizations.data.map((org) => (
														<NativeSelectOption key={org.id} value={org.id}>
															{org.name}
														</NativeSelectOption>
													))}
												</NativeSelect>
												{invalid ? <FieldError>{t("selectOrganization")}</FieldError> : null}
											</Field>
										);
									}}
								</form.Field>
							</FieldGroup>
						</form>
					) : null}
					{!failed && step === "continue" ? <p role="status">{t("loading")}</p> : null}
					{!failed && step === "consent" && consent.data ? (
						<>
							<p>{t("client", { name: consent.data.client.client_name || clientId! })}</p>
							{callbackOrigin ? <p>{t("callback", { origin: callbackOrigin })}</p> : null}
							<p>
								{t("account", {
									email: session.data!.user.email,
									organization: consent.data.organization.name,
								})}
							</p>
							<p>{t("access")}</p>
							<p>{t("billing")}</p>
							<p>{t("revoke")}</p>
						</>
					) : null}
				</CardContent>
				<CardFooter className="flex gap-3">
					{!failed && step === "organization" && organizations.data ? (
						<form.Subscribe selector={(state) => state.isSubmitting}>
							{(submitting) => (
								<Button
									type="submit"
									form="mcp-organization"
									disabled={submitting || organizations.data.length === 0}
								>
									{t("continue")}
								</Button>
							)}
						</form.Subscribe>
					) : null}
					{!failed && step === "consent" && consent.data ? (
						<>
							<Button disabled={pending} onClick={() => approve(true)}>
								{t("approve")}
							</Button>
							<Button variant="outline" disabled={pending} onClick={() => approve(false)}>
								{t("deny")}
							</Button>
						</>
					) : null}
				</CardFooter>
			</Card>
		</main>
	);
}
